import React, { useState, useEffect } from "react";
import { DataGrid, GridColDef, GridToolbar } from "@mui/x-data-grid";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Chip,
  Tab,
  Tabs,
} from "@mui/material";
import moment from "moment";
import toast from "react-hot-toast";
import { useQuery, useMutation } from "@apollo/client";
import { GET_ARCHIVES } from "../../../graphql/Admins/query";
import { RESTORE_ARCHIVE } from "../../../graphql/Admins/mutation";
import { useAppThunkDispatch } from "../../../Redux/hooks";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`archive-tabpanel-${index}`}
      aria-labelledby={`archive-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const ArchiveTable = () => {
  const dispatch = useAppThunkDispatch();
  const [tabValue, setTabValue] = useState(0);
  const [restoreModalOpen, setRestoreModalOpen] = useState(false);
  const [selectedArchive, setSelectedArchive] = useState<any>(null);
  const [restoreDescription, setRestoreDescription] = useState("");

  // Fetch archives with filter based on tab
  const archiveFilter = tabValue === 0 ? { archiveType: "USER" } : { archiveType: "ENTITY" };
  const { data: archivesData, loading, error, refetch } = useQuery(GET_ARCHIVES, {
    variables: { filter: archiveFilter, page: 1, limit: 100 },
    fetchPolicy: "cache-and-network",
  });
  


  const [restoreArchive] = useMutation(RESTORE_ARCHIVE);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleRestoreClick = (archive: any) => {
    setSelectedArchive(archive);
    setRestoreModalOpen(true);
  };

  const handleRestoreConfirm = async () => {
    if (!selectedArchive) return;

    try {
      const loadingToast = toast.loading("Restoring...");
      
      await restoreArchive({
        variables: {
          input: {
            archiveId: selectedArchive._id,
            description: restoreDescription || "Restored from archives",
          },
        },
      });

      toast.dismiss(loadingToast);
      toast.success("Successfully restored!");
      setRestoreModalOpen(false);
      setRestoreDescription("");
      setSelectedArchive(null);
      refetch();
    } catch (error) {
      toast.dismiss();
      toast.error("Failed to restore");
      console.error("Restore error:", error);
    }
  };

  const getReasonColor = (reason: string) => {
    switch (reason) {
      case "DUPLICATE":
        return "warning";
      case "INACTIVE":
        return "error";
      case "DATA_CLEANUP":
        return "info";
      case "USER_REQUEST":
        return "primary";
      case "COMPLIANCE":
        return "secondary";
      default:
        return "default";
    }
  };

  const parseDeletedData = (row: any) => {
    try {
      // Get name from deletedData
      if (row.deletedData) {
        const data = typeof row.deletedData === 'string' ? JSON.parse(row.deletedData) : row.deletedData;
        if (data && Array.isArray(data)) {
          console.log('Parsing name from:', data);
          const nameField = data.find(item => 
            item.Key === 'name' || item.key === 'name' || 
            item.Key === 'Name' || item.key === 'Name'
          );
          console.log('Found name field:', nameField);
          if (nameField && (nameField.value || nameField.Value)) return nameField.value || nameField.Value;
        }
      }
      
      return 'Unknown';
    } catch (error) {
      console.error("Error parsing deleted data:", error);
      return "Unknown";
    }
  };

  const parseDeletedEmail = (row: any) => {
    try {
      // Get email from deletedData
      if (row.deletedData) {
        const data = typeof row.deletedData === 'string' ? JSON.parse(row.deletedData) : row.deletedData;
        if (data && Array.isArray(data)) {
          const emailField = data.find(item => 
            item.Key === 'email' || item.key === 'email' || 
            item.Key === 'Email' || item.key === 'Email'
          );
          if (emailField && (emailField.value || emailField.Value)) return emailField.value || emailField.Value;
        }
      }
      
      return 'Unknown';
    } catch (error) {
      console.error("Error parsing deleted email:", error);
      return "Unknown";
    }
  };

  const parseDeletedContact = (row: any) => {
    try {
      // Get contact from deletedData
      if (row.deletedData) {
        const data = typeof row.deletedData === 'string' ? JSON.parse(row.deletedData) : row.deletedData;
        if (data && Array.isArray(data)) {
          const contactField = data.find(item => 
            item.Key === 'contact' || item.key === 'contact' || 
            item.Key === 'Contact' || item.key === 'Contact' ||
            item.Key === 'phone' || item.key === 'phone' ||
            item.Key === 'Phone' || item.key === 'Phone'
          );
          if (contactField && (contactField.value || contactField.Value)) return contactField.value || contactField.Value;
        }
      }
      
      return 'Unknown';
    } catch (error) {
      console.error("Error parsing deleted contact:", error);
      return "Unknown";
    }
  };

  const parseDeletedRole = (row: any) => {
    try {
      // Get role from deletedData
      if (row.deletedData) {
        const data = typeof row.deletedData === 'string' ? JSON.parse(row.deletedData) : row.deletedData;
        if (data && Array.isArray(data)) {
          // For users, look for 'role' field
          const roleField = data.find(item => 
            item.Key === 'role' || item.key === 'role' || 
            item.Key === 'Role' || item.key === 'Role'
          );
          if (roleField && (roleField.value || roleField.Value)) return roleField.value || roleField.Value;
          
          // For entities, look for entity-specific fields
          const individualTypeField = data.find(item => 
            item.Key === 'individualType' || item.key === 'individualType' || 
            item.Key === 'IndividualType' || item.key === 'IndividualType'
          );
          if (individualTypeField && (individualTypeField.value || individualTypeField.Value)) return individualTypeField.value || individualTypeField.Value;
          
          const organizationTypeField = data.find(item => 
            item.Key === 'organizationType' || item.key === 'organizationType' || 
            item.Key === 'OrganizationType' || item.key === 'OrganizationType'
          );
          if (organizationTypeField && (organizationTypeField.value || organizationTypeField.Value)) return organizationTypeField.value || organizationTypeField.Value;
          
          // Fallback to generic type fields
          const typeField = data.find(item => 
            item.Key === 'type' || item.key === 'type' || 
            item.Key === 'Type' || item.key === 'Type'
          );
          if (typeField && (typeField.value || typeField.Value)) return typeField.value || typeField.Value;
        }
      }
      
      return 'Unknown';
    } catch (error) {
      console.error("Error parsing deleted role:", error);
      return "Unknown";
    }
  };

  const parseDeletedAddress = (row: any) => {
    try {
      // Get address from deletedData
      if (row.deletedData) {
        const data = typeof row.deletedData === 'string' ? JSON.parse(row.deletedData) : row.deletedData;
        if (data && Array.isArray(data)) {
          const addressField = data.find(item => 
            item.Key === 'address' || item.key === 'address' || 
            item.Key === 'Address' || item.key === 'Address'
          );
          if (addressField && (addressField.value || addressField.Value)) return addressField.value || addressField.Value;
        }
      }
      
      return 'Unknown';
    } catch (error) {
      console.error("Error parsing deleted address:", error);
      return "Unknown";
    }
  };

  // Columns for Archived Users
  const userColumns: GridColDef[] = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      renderCell: (params: any) => {
        return <Typography sx={{fontSize:"14px"}}>{parseDeletedData(params.row)}</Typography>;
      },
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      renderCell: (params: any) => {
        return <Typography sx={{fontSize:"14px"}}>{parseDeletedEmail(params.row)}</Typography>;
      },
    },
    {
      field: "contact",
      headerName: "Contact",
      flex: 1,
      renderCell: (params: any) => {
        return <Typography sx={{fontSize:"14px"}}>{parseDeletedContact(params.row)}</Typography>;
      },
    },
    {
      field: "role",
      headerName: "Role",
      flex: 1,
      renderCell: (params: any) => {
        return <Typography sx={{fontSize:"14px"}}>{parseDeletedRole(params.row)}</Typography>;
      },
    },
    {
      field: "reason",
      headerName: "Reason",
      flex: 1,
      renderCell: (params: any) => {
        return (
          <Chip
            label={params.row.reason}
            color={getReasonColor(params.row.reason) as any}
            size="small"
          />
        );
      },
    },
    {
      field: "entityType",
      headerName: "Entity Type",
      flex: 1,
      renderCell: (params: any) => {
        return <Chip label={params.row.originalType} variant="outlined" size="small" />;
      },
    },
  ];

  // Columns for Archived Entities (Organizations/Individuals)
  const entityColumns: GridColDef[] = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      renderCell: (params: any) => {
        return <Typography sx={{fontSize:"14px"}}>{parseDeletedData(params.row)}</Typography>;
      },
    },
    {
      field: "address",
      headerName: "Address",
      flex: 1,
      renderCell: (params: any) => {
        return <Typography sx={{fontSize:"14px"}}>{parseDeletedAddress(params.row)}</Typography>;
      },
    },
    {
      field: "contact",
      headerName: "Contact",
      flex: 1,
      renderCell: (params: any) => {
        return <Typography sx={{fontSize:"14px"}}>{parseDeletedContact(params.row)}</Typography>;
      },
    },
    {
      field: "role",
      headerName: "Role",
      flex: 1,
      renderCell: (params: any) => {
        return <Typography sx={{fontSize:"14px"}}>{parseDeletedRole(params.row)}</Typography>;
      },
    },
    {
      field: "reason",
      headerName: "Reason",
      flex: 1,
      renderCell: (params: any) => {
        return (
          <Chip
            label={params.row.reason}
            color={getReasonColor(params.row.reason) as any}
            size="small"
          />
        );
      },
    },
    {
      field: "entityType",
      headerName: "Entity Type",
      flex: 1,
      renderCell: (params: any) => {
        return <Chip label={params.row.originalType} variant="outlined" size="small" />;
      },
    },
  ];

  const archives = archivesData?.archives || [];

  // Show message when no archives are found
  const showNoDataMessage = !loading && !error && archives.length === 0;
  
  


  return (
    <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
    
      {error && (
        <Box sx={{ mb: 2, p: 2, bgcolor: "#ffebee", borderRadius: 1, border: "1px solid #f44336" }}>
          <Typography color="#d32f2f" fontWeight="bold">
            ❌ Error Loading Archives
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {error.message}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Please check the console for more details or contact support if the issue persists.
          </Typography>
        </Box>
      )}
      
      <Box sx={{ borderBottom: 1,marginLeft: "13px", borderColor: "divider" }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="archive tabs">
          <Tab label="Archived Users" />
          <Tab label="Archived Entities" />
        </Tabs>
      </Box>

      <CustomTabPanel value={tabValue} index={0}>
        {showNoDataMessage ? (
          <Box sx={{ textAlign: "center", mt: 4 }}>
            <Typography variant="h6" color="text.secondary">
              No archived users found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Deleted users will appear here
            </Typography>
          </Box>
        ) : (
          <Box sx={{ width: '100%',backgroundColor:"white", borderRadius:"10px" }}>
            <DataGrid
              rows={archives}
              columns={userColumns}
              getRowId={(row) => row._id}
              loading={loading}
              pageSize={25}
              disableSelectionOnClick
              autoHeight
              components={{ Toolbar: GridToolbar }}
              componentsProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 500 },
                },
              }}
              sx={{
                "& .MuiDataGrid-cell": {
                  borderBottom: "1px solid #e0e0e0",
                  backgroundColor: "#ffffff",
                },
                "& .MuiDataGrid-row": {
                  backgroundColor: "#ffffff",
                },
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#f5f5f5",
                  borderBottom: "2px solid #e0e0e0",
                },
              }}
            />
          </Box>
        )}
      </CustomTabPanel>

      <CustomTabPanel value={tabValue} index={1}>
        {showNoDataMessage ? (
          <Box sx={{ textAlign: "center", mt: 4 }}>
            <Typography variant="h6" color="text.secondary">
              No archived entities found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Deleted entities will appear here
            </Typography>
          </Box>
        ) : (
          <Box sx={{width: "100%", backgroundColor:"white", borderRadius:"10px"}}>
            <DataGrid
              rows={archives}
              columns={entityColumns}
              autoHeight
              getRowId={(row) => row._id}
              loading={loading}
              pageSize={25}
              disableSelectionOnClick
              components={{ Toolbar: GridToolbar }}
              componentsProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 500 },
                },
              }}
              sx={{
                "& .MuiDataGrid-cell": {
                  borderBottom: "1px solid #e0e0e0",
                  backgroundColor: "#ffffff",
                },
                "& .MuiDataGrid-row": {
                  backgroundColor: "#ffffff",
                },
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#f5f5f5",
                  borderBottom: "2px solid #e0e0e0",
                },
              }}
            />
          </Box>
        )}
      </CustomTabPanel>

      {/* Restore Confirmation Dialog */}
      <Dialog
        open={restoreModalOpen}
        onClose={() => setRestoreModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Restore Archive</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Are you sure you want to restore this item?
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Name: {selectedArchive && parseDeletedData(selectedArchive.deletedData)}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Type: {selectedArchive?.originalType}
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Restore Description (Optional)"
            fullWidth
            variant="outlined"
            value={restoreDescription}
            onChange={(e) => setRestoreDescription(e.target.value)}
            placeholder="Reason for restoration..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreModalOpen(false)}>Cancel</Button>
          <Button onClick={handleRestoreConfirm} variant="contained">
            Restore
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ArchiveTable; 